export interface ReadingCandidate {
  key: string;
  wordCount: number;
  requiredMilliseconds: number;
  creditedMilliseconds: number;
  visibleFraction: number;
  exposedFraction: number;
}

export interface ReadingClock {
  now: number;
  previous: number;
  visible: boolean;
  focused: boolean;
  printing: boolean;
  lastActivity: number;
}

export interface ReadingCredit {
  key: string;
  creditedMilliseconds: number;
}

export interface ReadingRect { left: number; top: number; right: number; bottom: number }
export interface ReadingPositionCandidate { key: string; top: number; bottom: number }
export interface ReadingPositionDwell { key: string; milliseconds: number; engaged: boolean }

const fraction = (value: number) => Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;

export function allowedReadingElapsed(clock: ReadingClock, idleLimit = 60_000, gapLimit = 2_500): number {
  const elapsed = clock.now - clock.previous;
  const idle = clock.now - clock.lastActivity;
  return clock.visible && clock.focused && !clock.printing
    && Number.isFinite(elapsed) && elapsed > 0 && elapsed <= gapLimit
    && Number.isFinite(idle) && idle >= 0 && idle < idleLimit
    ? Math.floor(Math.min(elapsed, Math.max(0, idleLimit - Math.max(0, clock.previous - clock.lastActivity)))) : 0;
}

/** Spends one elapsed-time budget in the supplied reading order, returning absolute cumulative totals. */
export function allocateReadingBudget(candidates: readonly ReadingCandidate[], elapsedMilliseconds: number): ReadingCredit[] {
  let budget = Number.isFinite(elapsedMilliseconds) ? Math.max(0, Math.floor(elapsedMilliseconds)) : 0;
  const unique = new Set<string>();
  const credits: ReadingCredit[] = [];
  for (const candidate of candidates) {
    if (budget <= 0) break;
    if (!candidate.key || unique.has(candidate.key)) continue;
    unique.add(candidate.key);
    const { requiredMilliseconds: required, creditedMilliseconds: credited, wordCount } = candidate;
    if (![required, credited, wordCount].every(Number.isFinite) || required <= 0 || credited < 0 || credited > required || wordCount <= 0
      || fraction(candidate.visibleFraction) <= 0) continue;
    const baseline = Math.floor(credited);
    const ceiling = Math.max(baseline, Math.floor(required * fraction(candidate.exposedFraction)));
    const increment = Math.min(budget, ceiling - baseline);
    if (increment <= 0) continue;
    credits.push({ key: candidate.key, creditedMilliseconds: baseline + increment });
    budget -= increment;
  }
  return credits;
}

export function intersectReadingRects(a: ReadingRect, b: ReadingRect): ReadingRect {
  return { left: Math.max(a.left, b.left), top: Math.max(a.top, b.top), right: Math.min(a.right, b.right), bottom: Math.min(a.bottom, b.bottom) };
}

export function inMainReadingViewport(block: ReadingRect, viewport: ReadingRect, clip: ReadingRect): boolean {
  const height = viewport.bottom - viewport.top;
  const band = { ...viewport, top: viewport.top + height * .2, bottom: viewport.bottom - height * .2 };
  const visible = intersectReadingRects(block, intersectReadingRects(band, clip));
  const blockHeight = block.bottom - block.top;
  const blockWidth = block.right - block.left;
  return height > 0 && blockHeight > 0 && blockWidth > 0
    && visible.bottom - visible.top >= Math.min(blockHeight, band.bottom - band.top) * .5
    && visible.right - visible.left >= Math.min(blockWidth, viewport.right - viewport.left) * .5;
}

export function visibleReadingFraction(rectangles: readonly ReadingRect[], clip: ReadingRect): number {
  const area = (rect: ReadingRect) => Math.max(0, rect.right - rect.left) * Math.max(0, rect.bottom - rect.top);
  const total = rectangles.reduce((sum, rect) => sum + area(rect), 0);
  return total > 0 ? fraction(rectangles.reduce((sum, rect) => sum + area(intersectReadingRects(rect, clip)), 0) / total) : 0;
}

export function readingWordSpans(text: string): { text: string; start: number; end: number }[] {
  // Unicode White_Space matches .NET here: NEXT LINE is whitespace and BOM is not.
  return [...text.matchAll(/\P{White_Space}+/gu)].map(match => ({ text: match[0], start: match.index!, end: match.index! + match[0].length }));
}

export function mergeAbsoluteReadingCredit(local: number, remote: number, required: number): number {
  const valid = (value: number) => Number.isFinite(value) ? Math.max(0, Math.min(required, Math.floor(value))) : 0;
  return Math.max(valid(local), valid(remote));
}

export function selectReadingPosition(candidates: readonly ReadingPositionCandidate[], readingLine: number): string | undefined {
  if (!Number.isFinite(readingLine)) return;
  const eligible = candidates.filter(candidate => candidate.key && Number.isFinite(candidate.top)
    && Number.isFinite(candidate.bottom) && candidate.bottom > candidate.top);
  const distance = (candidate: ReadingPositionCandidate) => Math.max(candidate.top - readingLine, readingLine - candidate.bottom, 0);
  return [...eligible].sort((a, b) => distance(a) - distance(b)
    || Math.abs((a.top + a.bottom) / 2 - readingLine) - Math.abs((b.top + b.bottom) / 2 - readingLine))[0]?.key;
}

export function observeReadingPosition(previous: ReadingPositionDwell | undefined, key: string | undefined,
  elapsedMilliseconds: number, requiredMilliseconds = 1000): ReadingPositionDwell | undefined {
  if (!key || !Number.isFinite(elapsedMilliseconds) || elapsedMilliseconds <= 0 || elapsedMilliseconds > 2500) return;
  const total = (previous?.key === key ? previous.milliseconds : 0) + elapsedMilliseconds;
  const engaged = total >= requiredMilliseconds;
  return { key, milliseconds: engaged ? 0 : total, engaged };
}
